import { useQuery } from "@tanstack/react-query";
import { Package } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";

function formatAddress(a) {
  if (!a) return null;
  return [a.name, a.line1, a.line2, [a.city, a.region, a.postal].filter(Boolean).join(", "), a.country]
    .filter(Boolean)
    .join(" · ");
}

export default function SellerOrders() {
  const { formatMoney } = useLocale();
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["seller-orders"],
    queryFn: async () => (await api.get("/seller/orders")).data,
    refetchInterval: 8000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  if (isLoading) return <div className="max-w-4xl mx-auto px-6 pt-28 pb-24"><div className="h-64 shimmer" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Fulfillment</div>
      <h1 className="text-3xl font-semibold mb-10">Orders to ship</h1>
      {orders.length === 0 ? (
        <div className="py-20 text-center">
          <Package className="w-8 h-8 mx-auto text-aura-muted mb-4" />
          <p className="text-aura-muted">No orders yet.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="seller-orders-list">
          {orders.map((o) => (
            <div key={o.id} className="bg-aura-surface border border-aura-border p-6">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-aura-border">
                <div>
                  <div className="text-xs text-aura-muted">Order #{(o.id || "").slice(0, 8)}</div>
                  <div className="text-sm mt-1">{o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}</div>
                </div>
                <span className="text-[10px] tracking-luxe uppercase bg-aura-sand px-2.5 py-1">{o.status}</span>
              </div>
              <div className="space-y-3">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-12 h-16 bg-aura-sand overflow-hidden shrink-0">
                      {it.image && <img src={imgUrl(it.image)} alt={it.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 text-sm">{it.title}</div>
                    <div className="text-sm text-aura-muted">×{it.quantity}</div>
                    <div className="font-mono text-sm">{formatMoney(it.price, (o.currency || it.listing_currency || "usd").toUpperCase(), { convert: false })}</div>
                  </div>
                ))}
              </div>
              {o.shipping_address && (
                <p className="mt-4 text-sm text-aura-muted" data-testid={`ship-to-${o.id}`}>
                  Ship to {formatAddress(o.shipping_address)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
